'use client';

import type { ResultOf } from '@graphql-typed-document-node/core';
import { Checkbox } from '@savvy-kit/components/checkbox';
import IconButton from '@savvy-kit/components/icon-button';
import { SortableHeaderContent } from '@savvy-kit/components/table';
import { Ellipsis } from 'lucide-react';
import { formatDollarAmount } from 'src/utils/formatting';

import type { ColumnDef } from '@savvy/data-table';
import { createColumnHelper } from '@savvy/data-table';

import type { SelectedClient } from '../tax-utils';
import type { TaxOverviewTableBodyRowFragment } from './tax-overview-table-body-row';
import type { TaxOverviewTableColumn } from './tax-overview-table-columns';
import { castToTaxEnrollmentType, formatTaxEnrollment } from '../tax-utils';
import { TaxStatusBadge } from './tax-status-badge';

export type TaxRow = ResultOf<typeof TaxOverviewTableBodyRowFragment> & {
  readonly clientId: string;
  readonly taxEnrollmentId: string | null;
};

export type SortParam = {
  readonly isSorted: boolean;
  readonly direction: 'ASC' | 'DESC';
  readonly onSort: () => void;
};

export type TaxColumnSortParams = {
  readonly client: SortParam;
  readonly filingType: SortParam;
  readonly taxYear: SortParam;
  readonly taxStatus: SortParam;
  readonly taxPrice: SortParam;
};

export type TaxColumnSelectionParams = {
  readonly selectedClients: readonly SelectedClient[];
  readonly allSelected: boolean;
  readonly someSelected: boolean;
  readonly onSelectAll: () => void;
  readonly onToggleRow: (client: SelectedClient, checked: boolean) => void;
};

export const COLUMN_ID_TO_DISPLAY_NAME: Readonly<Partial<Record<string, TaxOverviewTableColumn>>> = {
  client: 'Client',
  filingType: 'Filing Type',
  taxYear: 'Tax Year',
  taxStatus: 'Tax Status',
  taxPrice: 'Tax Price',
};

export const DISPLAY_NAME_TO_COLUMN_ID: Readonly<Record<TaxOverviewTableColumn, string>> = {
  Client: 'client',
  'Filing Type': 'filingType',
  'Tax Year': 'taxYear',
  'Tax Status': 'taxStatus',
  'Tax Price': 'taxPrice',
};

const columnHelper = createColumnHelper<TaxRow>();

function SortHeader({ label, sort }: { readonly label: string; readonly sort: SortParam }) {
  return (
    <SortableHeaderContent sortDirection={sort.direction} isSorted={sort.isSorted} onClick={sort.onSort}>
      {label}
    </SortableHeaderContent>
  );
}

export function createTaxColumns(
  sorts: TaxColumnSortParams,
  visibleColumns: readonly TaxOverviewTableColumn[],
  selection: TaxColumnSelectionParams,
  onEditClick: (salesforceId: string | null) => void,
  enableTaxFiling: boolean,
): ColumnDef<TaxRow>[] {
  let checkboxState: boolean | 'indeterminate' = false;
  if (selection.allSelected) {
    checkboxState = true;
  } else if (selection.someSelected) {
    checkboxState = 'indeterminate';
  }

  const selectColumn: ColumnDef<TaxRow> = columnHelper.display({
    id: 'select',
    header: () => (
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox aria-label="Select all" checked={checkboxState} onCheckedChange={() => selection.onSelectAll()} />
      </div>
    ),
    cell: ({ row }) => {
      const checkedClient: SelectedClient = {
        id: row.original.rowId,
        clientId: row.original.clientId,
        taxEnrollmentId: row.original.taxEnrollmentId,
        salesforceId: row.original.salesforceId,
      };
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            aria-label="Select row"
            checked={selection.selectedClients.some((c) => c.id === row.original.rowId)}
            onCheckedChange={(checked) => selection.onToggleRow(checkedClient, !!checked)}
          />
        </div>
      );
    },
    size: 40,
    minSize: 40,
    enableResizing: false,
    meta: { columnOrdering: { enabled: false } },
  });

  const dataColumns: Partial<Record<string, ColumnDef<TaxRow>>> = {
    client: columnHelper.display({
      id: 'client',
      header: () => <SortHeader label="Client" sort={sorts.client} />,
      cell: ({ row }) => (
        <p>{row.original.enrollmentType === 'savvy_joint' ? row.original.householdName : row.original.clientName}</p>
      ),
      size: 296,
      minSize: 200,
      enableResizing: true,
    }),
    filingType: columnHelper.display({
      id: 'filingType',
      header: () => <SortHeader label="Filing Type" sort={sorts.filingType} />,
      cell: ({ row }) => (
        <p>
          {row.original.enrollmentType !== null
            ? formatTaxEnrollment(castToTaxEnrollmentType(row.original.enrollmentType))
            : '-'}
        </p>
      ),
      size: 175,
      minSize: 120,
      enableResizing: true,
    }),
    taxYear: columnHelper.display({
      id: 'taxYear',
      header: () => <SortHeader label="Tax Year" sort={sorts.taxYear} />,
      cell: ({ row }) => <p>{row.original.taxYear ?? '-'}</p>,
      size: 100,
      minSize: 80,
      enableResizing: true,
    }),
    taxStatus: columnHelper.display({
      id: 'taxStatus',
      header: () => <SortHeader label="Tax Status" sort={sorts.taxStatus} />,
      cell: ({ row }) => (
        <TaxStatusBadge
          enrollmentStatus={row.original.enrollmentStatus}
          enrollmentType={row.original.enrollmentType}
          transcriptStatus={row.original.transcriptStatus}
          federalReturnStatus={row.original.federalReturnStatus}
          aprilAppStatus={row.original.aprilAppStatus}
          stateFilings={row.original.stateFilings}
          taxFilingEnabled={enableTaxFiling}
        />
      ),
      size: 200,
      minSize: 140,
      enableResizing: true,
    }),
    taxPrice: columnHelper.display({
      id: 'taxPrice',
      header: () => <SortHeader label="Tax Price" sort={sorts.taxPrice} />,
      cell: ({ row }) => <p>{row.original.price !== null ? formatDollarAmount(row.original.price, true) : '-'}</p>,
      size: 120,
      minSize: 80,
      enableResizing: true,
    }),
  };

  const actionsColumn: ColumnDef<TaxRow> = columnHelper.display({
    id: 'actions',
    header: () => null,
    cell: ({ row }) =>
      !enableTaxFiling ? (
        <IconButton
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onEditClick(row.original.salesforceId);
          }}
        >
          <Ellipsis />
        </IconButton>
      ) : null,
    size: 48,
    minSize: 48,
    enableResizing: false,
    meta: { columnOrdering: { enabled: false } },
  });

  const visibleDataColumns = visibleColumns
    .map((col) => dataColumns[DISPLAY_NAME_TO_COLUMN_ID[col]])
    .filter((col): col is ColumnDef<TaxRow> => col !== undefined);

  return [selectColumn, ...visibleDataColumns, actionsColumn];
}
